/**
 * ChatInput - 训练广场单智能体对话输入组件
 *
 * 独立的输入框组件，支持：
 * - 多行文本输入（自动扩展高度）
 * - Enter 发送 / Shift+Enter 换行
 * - 发送/停止按钮
 * - 禁用状态
 * - 语音输入（与团队训练 MessageInput 同一套 FunASR 逻辑）
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SendIcon from '@mui/icons-material/Send';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import FunASRClient from '../../utils/FunASRClient';

const ChatInput = ({ onSend, isStreaming, onStop, disabled, placeholder, extraActions = null }) => {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);
  const [isVoiceProcessing, setIsVoiceProcessing] = useState(false);
  const [voiceError, setVoiceError] = useState('');
  const textareaRef = useRef(null);
  const voiceRecognitionRef = useRef(null);

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const maxHeight = 160;
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [input, adjustHeight]);

  useEffect(() => {
    const recognition = new FunASRClient({ stopTimeoutMs: 6000 });
    recognition.onstart = () => {
      setIsVoiceRecording(true);
      setIsVoiceProcessing(false);
      setVoiceError('');
    };
    recognition.onend = () => {
      setIsVoiceRecording(false);
      setIsVoiceProcessing(false);
    };
    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript || '';
      if (!transcript.trim()) return;
      setInput((prev) => (prev ? `${prev} ${transcript}` : transcript));
      textareaRef.current?.focus();
    };
    recognition.onerror = (event) => {
      console.error('[AgentChat ChatInput] 语音识别错误:', event.error);
      setIsVoiceRecording(false);
      setIsVoiceProcessing(false);
      setVoiceError('语音识别暂不可用，请检查麦克风权限或稍后重试。');
    };
    voiceRecognitionRef.current = recognition;
    return () => {
      try {
        voiceRecognitionRef.current?.dispose?.();
      } catch (_) {}
      voiceRecognitionRef.current = null;
    };
  }, []);

  const handleSubmit = useCallback((e) => {
    e?.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || isStreaming || disabled) return;
    onSend(trimmed);
    setInput('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [input, isStreaming, disabled, onSend]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  const handleVoiceClick = () => {
    if (!voiceRecognitionRef.current || disabled || isStreaming) return;
    setVoiceError('');
    if (isVoiceRecording) {
      voiceRecognitionRef.current.stop();
      setIsVoiceProcessing(true);
      return;
    }
    try {
      voiceRecognitionRef.current.start();
    } catch (error) {
      console.error('[AgentChat ChatInput] 启动语音失败:', error);
      setVoiceError('无法启动麦克风，请检查浏览器权限。');
    }
  };

  const handleCancelVoice = () => {
    try {
      voiceRecognitionRef.current?.abort?.();
    } catch (_) {}
    setIsVoiceRecording(false);
    setIsVoiceProcessing(false);
    setVoiceError('');
  };

  const canSend = input.trim().length > 0 && !isStreaming && !disabled;
  const inputPlaceholder = `${placeholder || t('chat_input_placeholder')} · Enter 发送 / Shift+Enter 换行`;
  const voiceDisabled = disabled || isStreaming || isVoiceProcessing;

  return (
    <form className="agent-chat-input-form" onSubmit={handleSubmit}>
      <div className="agent-chat-input-wrapper">
        <div className="agent-chat-input-field">
          <textarea
            ref={textareaRef}
            className="agent-chat-textarea"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={inputPlaceholder}
            disabled={disabled}
            rows={1}
          />
          {(isVoiceRecording || isVoiceProcessing || voiceError) && (
            <div className={`agent-chat-voice-status ${voiceError ? 'error' : ''}`}>
              <span>
                {voiceError || (isVoiceProcessing ? '正在识别语音，结果会填入输入框...' : '正在录音，再次点击麦克风结束')}
              </span>
              <button type="button" onClick={handleCancelVoice}>
                取消
              </button>
            </div>
          )}
        </div>
        <div className="agent-chat-input-actions">
          {extraActions}
          <button
            type="button"
            className={`agent-chat-voice-btn ${isVoiceRecording ? 'recording' : ''}`}
            onClick={handleVoiceClick}
            disabled={voiceDisabled}
            title={isVoiceRecording ? '结束录音并转写' : '语音输入'}
          >
            {isVoiceProcessing ? '识别中' : isVoiceRecording ? '结束' : '语音'}
          </button>
          {isStreaming ? (
            <button
              type="button"
              className="agent-chat-stop-btn"
              onClick={onStop}
              title={t('chat_input_stop')}
            >
              <StopCircleOutlinedIcon fontSize="small" />
            </button>
          ) : (
            <button
              type="submit"
              className={`agent-chat-send-btn ${canSend ? 'active' : ''}`}
              disabled={!canSend}
              title={t('chat_input_send')}
            >
              <SendIcon fontSize="small" />
            </button>
          )}
        </div>
      </div>
    </form>
  );
};

export default React.memo(ChatInput);
